"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetCombobulator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const CombobulationDimension_1 = require("./CombobulationDimension");
const NetUnionFind_1 = require("./NetUnionFind");
class NetCombobulator {
    static { this.DEFAULT_DOMAIN = 'wire'; }
    static { this.PRINT_ENTRIES = false; }
    constructor(netRefCtx, namingStrategy, instance, opts = {}) {
        this.netRefCtx = netRefCtx;
        this.namingStrategy = namingStrategy;
        this.instance = instance;
        this.opts = opts;
        this.dimensions = new Map();
        this._nets = new Map();
        this.unionFind = new NetUnionFind_1.NetUnionFind();
        this.computedNets = false;
        this.defaultDimension = new CombobulationDimension_1.CombobulationDimension(opts.defaultDimension ?? NetCombobulator.DEFAULT_DOMAIN, {
            explicitWireIntersections: opts.explicitWireIntersections,
            pointRoundingPrecision: opts.pointRoundingPrecision ?? 0.1,
            connectionTolerance: opts.connectionTolerance,
        });
    }
    get pointRoundingPrecision() {
        return this.defaultDimension.pointRoundingPrecision;
    }
    get explicitWireIntersections() {
        return this.opts.explicitWireIntersections ?? false;
    }
    *iterateNets() {
        yield* this._nets.values();
    }
    *iterateWireObjects() {
        const seen = new Set();
        for (const dimension of this.allDimensions()) {
            for (const obj of dimension.iterateWireObjects()) {
                if (!seen.has(obj)) {
                    seen.add(obj);
                    yield obj;
                }
            }
        }
    }
    *iterateLabelObjects(labelType) {
        const seen = new Set();
        for (const dimension of this.allDimensions()) {
            for (const obj of dimension.iterateLabelObjects(labelType)) {
                if (!seen.has(obj)) {
                    seen.add(obj);
                    yield obj;
                }
            }
        }
    }
    *iterateConnectionObjects() {
        const seen = new Set();
        for (const dimension of this.allDimensions()) {
            for (const obj of dimension.iterateConnectionObjects()) {
                if (!seen.has(obj)) {
                    seen.add(obj);
                    yield obj;
                }
            }
        }
    }
    *iterateAllObjects() {
        const seen = new Set();
        for (const dimension of this.allDimensions()) {
            for (const obj of dimension.iterateAllObjects()) {
                if (!seen.has(obj)) {
                    seen.add(obj);
                    yield obj;
                }
            }
        }
    }
    *allDimensions() {
        yield this.defaultDimension;
        for (const dimension of this.dimensions.values()) {
            yield dimension;
        }
    }
    hasDimension(dimensionName) {
        return this.dimensions.has(dimensionName);
    }
    getDimension(dimensionName) {
        if (dimensionName === this.defaultDimension.name) {
            return this.defaultDimension;
        }
        const dimension = this.dimensions.get(dimensionName);
        if (!dimension) {
            throw new Error(`Dimension ${dimensionName} not found`);
        }
        return dimension;
    }
    getOrCreateDimension(dimensionName) {
        if (dimensionName === this.defaultDimension.name) {
            return this.defaultDimension;
        }
        if (!this.dimensions.has(dimensionName)) {
            this.dimensions.set(dimensionName, new CombobulationDimension_1.CombobulationDimension(dimensionName, {
                explicitWireIntersections: this.opts.explicitWireIntersections,
                pointRoundingPrecision: this.defaultDimension.pointRoundingPrecision,
                connectionTolerance: this.defaultDimension.connectionTolerance,
            }));
        }
        return this.dimensions.get(dimensionName);
    }
    addWire(wire) {
        const fromEndpoint = NetCombobulator.toEndpoint(wire.from, this.defaultDimension.name);
        const toEndpoint = NetCombobulator.toEndpoint(wire.to, this.defaultDimension.name);
        const internalWire = {
            from: fromEndpoint.position,
            to: toEndpoint.position,
            dimensions: Array.from(new Set([fromEndpoint.dimension, toEndpoint.dimension])),
            strategyMetadata: wire.strategyMetadata,
        };
        const endpoints = [fromEndpoint, toEndpoint];
        const netRef = this.getUniqueNetRef();
        const obj = {
            wire: internalWire,
            netRef: netRef,
            endpoints,
        };
        const transformedName = this.namingStrategy.transformNetName(obj, this.instance);
        const connectivityName = this.namingStrategy.transformConnectivityName?.(obj, this.instance) ?? transformedName;
        if (connectivityName) {
            netRef.setName(connectivityName);
            obj.appliesNetName = connectivityName;
        }
        this.addEndpoints(endpoints, obj);
        this.getOrCreateDimension(fromEndpoint.dimension).addWireObject(obj);
        if (fromEndpoint.dimension !== toEndpoint.dimension) {
            this.getOrCreateDimension(toEndpoint.dimension).addWireObject(obj);
        }
        this.autoAssignNet(obj);
        return netRef;
    }
    setLabel(positionOrEndpoints, originalLabel, type, strategyMetadata, options) {
        const endpoints = NetCombobulator.normalizeEndpoints(positionOrEndpoints);
        const position = endpoints[0].position;
        const typeCanName = this.namingStrategy.canLabelTypeNameNets(type, this.instance);
        const canNameNet = (options?.canNameNet ?? true) && typeCanName;
        const transformedLabel = this.getUniqueNetRef(canNameNet ? originalLabel : undefined);
        const label = {
            originalLabel,
            position,
            labelType: type,
            canNameNet: typeCanName,
            strategyMetadata,
        };
        const netObject = {
            netRef: transformedLabel,
            endpoints,
            label,
        };
        if (typeCanName) {
            const transformedName = this.namingStrategy.transformNetName(netObject, this.instance);
            if (canNameNet) {
                const connectivityName = options?.connectivityName ??
                    this.namingStrategy.transformConnectivityName?.(netObject, this.instance) ??
                    transformedName ??
                    originalLabel;
                netObject.appliesNetName = connectivityName;
                transformedLabel.setName(connectivityName);
            }
            label.canNameNet = canNameNet;
            label.formattedLabel = transformedName;
        }
        this.addEndpoints(endpoints, netObject);
        this.autoAssignNet(netObject);
        return transformedLabel;
    }
    addConnection(positionOrEndpoints, connection) {
        let endpoints = NetCombobulator.normalizeEndpoints(positionOrEndpoints);
        const position = endpoints[0].position;
        let netRef = this.getUniqueNetRef(connection.appliesNetName?.label);
        if (connection.skipPositionalCombobulation) {
            endpoints = [
                {
                    position,
                    dimension: `isolated::${connection.moduleRef}::${connection.pinRef}::${netRef.valueOf()}`,
                },
            ];
        }
        let appliesNetName;
        if (connection.appliesNetName) {
            const canLabelNameNets = this.namingStrategy.canLabelTypeNameNets(connection.appliesNetName?.labelType, this.instance);
            if (canLabelNameNets) {
                appliesNetName = connection.appliesNetName;
            }
        }
        const fullConnection = {
            netRef: netRef,
            endpoints,
            connection: { connection: { ...connection, appliesNetName }, position },
        };
        const transformedName = this.namingStrategy.transformNetName(fullConnection, this.instance);
        const connectivityName = this.namingStrategy.transformConnectivityName?.(fullConnection, this.instance) ?? transformedName;
        if (connection.appliesNetName && !connectivityName) {
            netRef = new sigma_engine_shared_1.NetRef(undefined, netRef.valueOf());
            fullConnection.netRef = netRef;
        }
        if (connectivityName) {
            netRef.setName(connectivityName);
            fullConnection.appliesNetName = connectivityName;
        }
        this.addEndpoints(endpoints, fullConnection);
        this.autoAssignNet(fullConnection);
        return netRef;
    }
    getObjsAtPosition(pos, filterDimension) {
        const results = new Set();
        for (const dimension of this.allDimensions()) {
            if (filterDimension && dimension.name !== filterDimension) {
                continue;
            }
            for (const obj of dimension.getObjectsAtPosition(pos)) {
                results.add(obj);
            }
        }
        return Array.from(results);
    }
    getConnectedWireGroups() {
        const objToGroup = new Map();
        for (const dimension of this.allDimensions()) {
            for (const group of dimension.getConnectedWireGroups()) {
                for (const obj of group) {
                    const existingGroup = objToGroup.get(obj);
                    if (existingGroup && existingGroup !== group) {
                        for (const mergedObj of group) {
                            existingGroup.add(mergedObj);
                            objToGroup.set(mergedObj, existingGroup);
                        }
                    }
                    else {
                        objToGroup.set(obj, group);
                    }
                }
            }
        }
        return new Set(objToGroup.values());
    }
    removeObject(obj) {
        for (const endpoint of obj.endpoints) {
            this.getOrCreateDimension(endpoint.dimension).removeObject(obj);
        }
        const net = obj.net;
        if (net) {
            const removeFrom = (arr, item) => {
                const idx = arr.indexOf(item);
                if (idx !== -1) {
                    arr.splice(idx, 1);
                }
            };
            removeFrom(net.netObjects, obj);
            if (obj.label) {
                removeFrom(net.labels, obj.label);
            }
            if (obj.wire) {
                removeFrom(net.wires, obj.wire);
            }
            if (obj.connection) {
                removeFrom(net.connections, obj.connection);
            }
            net.netRefs.delete(obj.netRef);
            if (net.netObjects.length === 0) {
                for (const [name, n] of this._nets.entries()) {
                    if (n === net) {
                        this._nets.delete(name);
                    }
                }
            }
            obj.net = undefined;
        }
    }
    mergeNets(a, b) {
        if (!b || a === b) {
            return a;
        }
        for (const ref of b.netRefs) {
            a.netRefs.add(ref);
        }
        if (b.wires.length > 0) {
            const wireSet = (a._wireSet ??= new Set(a.wires));
            for (const w of b.wires) {
                if (!wireSet.has(w)) {
                    wireSet.add(w);
                    a.wires.push(w);
                }
            }
        }
        if (b.labels.length > 0) {
            const labelSet = (a._labelSet ??= new Set(a.labels));
            for (const l of b.labels) {
                if (!labelSet.has(l)) {
                    labelSet.add(l);
                    a.labels.push(l);
                }
            }
        }
        if (b.connections.length > 0) {
            const connKeySet = (a._connKeySet ??= new Set(a.connections.map((c) => `${c.connection.moduleRef}\0${c.connection.pinRef}`)));
            for (const c of b.connections) {
                const key = `${c.connection.moduleRef}\0${c.connection.pinRef}`;
                if (!connKeySet.has(key)) {
                    connKeySet.add(key);
                    a.connections.push(c);
                }
            }
        }
        if (b.netObjects.length > 0) {
            const objSet = (a._objSet ??= new Set(a.netObjects));
            for (const o of b.netObjects) {
                if (!objSet.has(o)) {
                    objSet.add(o);
                    a.netObjects.push(o);
                }
            }
        }
        for (const [name, currentNet] of this._nets.entries()) {
            if (currentNet === b) {
                this._nets.set(name, a);
            }
        }
        for (const obj of b.netObjects) {
            obj.net = a;
        }
        b._objSet = undefined;
        b._wireSet = undefined;
        b._labelSet = undefined;
        b._connKeySet = undefined;
        return a;
    }
    static normalizeEndpoints(positionOrEndpoints) {
        if (Array.isArray(positionOrEndpoints) &&
            positionOrEndpoints.length > 0 &&
            typeof positionOrEndpoints[0] === 'object' &&
            !Array.isArray(positionOrEndpoints[0])) {
            return positionOrEndpoints;
        }
        return [{ position: positionOrEndpoints, dimension: NetCombobulator.DEFAULT_DOMAIN }];
    }
    getUniqueNetRef(initialName) {
        let name = initialName;
        if (!name) {
            name = `__UNNAMED_NET_${this.netRefCtx.getUniqueRefNo()}`;
        }
        return new sigma_engine_shared_1.NetRef(this.netRefCtx, name);
    }
    static toEndpoint(positionOrEndpoint, defaultDimension) {
        if (Array.isArray(positionOrEndpoint)) {
            return { position: positionOrEndpoint, dimension: defaultDimension };
        }
        return positionOrEndpoint;
    }
    addEndpoints(endpoints, obj) {
        for (const endpoint of endpoints) {
            this.getOrCreateDimension(endpoint.dimension).addPoint(endpoint.position, obj);
        }
    }
    autoAssignNet(obj) {
        if (!this.opts.autoAssignNets) {
            return;
        }
        const net = {
            name: this.getUniqueNetRef(),
            netRefs: new Set(),
            labels: [],
            wires: [],
            connections: [],
            netObjects: [],
            instance: this.instance,
        };
        this.collectObjectIntoNet(net, obj);
        this._nets.set(obj.netRef.valueOf(), net);
    }
    collectObjectIntoNet(net, obj) {
        obj.net = net;
        const objSet = (net._objSet ??= new Set(net.netObjects));
        if (obj.label) {
            const labelSet = (net._labelSet ??= new Set(net.labels));
            if (!labelSet.has(obj.label)) {
                labelSet.add(obj.label);
                net.labels.push(obj.label);
            }
        }
        if (!objSet.has(obj)) {
            objSet.add(obj);
            net.netObjects.push(obj);
        }
        const objNetRef = obj.netRef;
        if (objNetRef && !net.netRefs.has(objNetRef)) {
            net.netRefs.add(objNetRef);
        }
        if (obj.connection) {
            const connKeySet = (net._connKeySet ??= new Set(net.connections.map((c) => `${c.connection.moduleRef}\0${c.connection.pinRef}`)));
            const connKey = `${obj.connection.connection.moduleRef}\0${obj.connection.connection.pinRef}`;
            if (!connKeySet.has(connKey)) {
                connKeySet.add(connKey);
                net.connections.push(obj.connection);
            }
        }
        if (obj.wire) {
            const wireSet = (net._wireSet ??= new Set(net.wires));
            if (!wireSet.has(obj.wire)) {
                wireSet.add(obj.wire);
                net.wires.push(obj.wire);
            }
        }
    }
    getFinalizedNets() {
        if (!this.finalizedNets) {
            throw new Error('Nets not finalized yet');
        }
        return this.finalizedNets;
    }
    computeNets() {
        this._nets = new Map();
        this.rebuildUnionFind();
        const components = this.unionFind.getComponents();
        this.unionFind.clear();
        const labelNets = new Map();
        for (const [, component] of components) {
            let net = {
                name: this.getUniqueNetRef(),
                netRefs: new Set(),
                labels: [],
                wires: [],
                connections: [],
                netObjects: [],
                instance: this.instance,
            };
            for (const obj of component) {
                if (obj.net) {
                    net = this.mergeNets(net, obj.net);
                }
            }
            for (const obj of component) {
                this.collectObjectIntoNet(net, obj);
                if (obj.appliesNetName && this._nets.has(obj.appliesNetName)) {
                    net = this.mergeNets(net, this._nets.get(obj.appliesNetName));
                }
                if (obj.appliesNetName) {
                    const existingNet = labelNets.get(obj.appliesNetName);
                    if (existingNet) {
                        net = this.mergeNets(net, existingNet);
                    }
                    labelNets.set(obj.appliesNetName, net);
                }
            }
            net = this.addNet(net);
            for (const obj of component) {
                obj.net = net;
            }
        }
        this.finalizedNets = new Map();
        for (const net of this._nets.values()) {
            const name = this.getNetName(net);
            let finalNet = {
                ...net,
                name: this.netRefCtx.existingOrNewRef(name),
            };
            finalNet.netRefs.add(finalNet.name);
            if (this.finalizedNets.has(finalNet.name.valueOf())) {
                finalNet = this.mergeNets(finalNet, this.finalizedNets.get(finalNet.name.valueOf()));
            }
            for (const netRef of finalNet.netRefs) {
                netRef.setName(finalNet.name);
            }
            for (const obj of finalNet.netObjects) {
                obj.netRef.setName(finalNet.name);
                obj.net = finalNet;
            }
            this.finalizedNets.set(finalNet.name.valueOf(), finalNet);
        }
        this._nets = this.finalizedNets;
        this.computedNets = true;
        const finalizedNetSet = new Set(this.finalizedNets.values());
        for (const dimension of this.allDimensions()) {
            for (const { x, y, objects: netObjs } of dimension.iteratePointGroups()) {
                const uniqueNets = new Set(netObjs.map((o) => o.net).filter((n) => n !== undefined));
                if (uniqueNets.size > 1) {
                    throw new Error(`Multiple unmerged nets at position ${x}, ${y}`);
                }
                for (const obj of netObjs) {
                    if (!obj.net) {
                        throw new Error(`No net for object at ${x}, ${y}`);
                    }
                    const memberSet = obj.net._objSet;
                    if (memberSet ? !memberSet.has(obj) : !obj.net.netObjects.includes(obj)) {
                        throw new Error(`Net for object at ${x}, ${y} does not include object`);
                    }
                    if (!finalizedNetSet.has(obj.net)) {
                        throw new Error(`Net for object at ${x}, ${y} is not in the final net list`);
                    }
                }
            }
        }
        return this.finalizedNets;
    }
    rebuildUnionFind() {
        this.unionFind.clear();
        for (const dimension of this.allDimensions()) {
            for (const { objects } of dimension.iteratePointGroups()) {
                if (objects.length === 0) {
                    continue;
                }
                this.unionFind.makeSet(objects[0]);
                for (let i = 1; i < objects.length; i++) {
                    this.unionFind.makeSet(objects[i]);
                    this.unionFind.union(objects[0], objects[i]);
                }
            }
            for (const wireObj of dimension.iterateWireObjects()) {
                this.unionFind.makeSet(wireObj);
                const intersecting = dimension.getWireIntersecting(wireObj);
                if (intersecting) {
                    for (const obj of intersecting) {
                        this.unionFind.makeSet(obj);
                        this.unionFind.union(wireObj, obj);
                    }
                }
                const geometry = dimension.getEffectiveWireGeometry(wireObj);
                if (geometry) {
                    for (const pos of [geometry.from, geometry.to]) {
                        for (const obj of dimension.getObjectsAtPosition(pos)) {
                            this.unionFind.makeSet(obj);
                            this.unionFind.union(wireObj, obj);
                        }
                    }
                }
            }
        }
    }
    getNetAtPosition(pos) {
        if (!this.computedNets) {
            throw new Error(`Run computeNets first`);
        }
        const objs = this.getObjsAtPosition(pos);
        const allNets = new Set(objs.map((o) => o.netRef?.valueOf()).filter((v) => !!v));
        if (allNets.size > 1) {
            throw new Error(`Multiple unmerged nets at position ${pos}`);
        }
        if (objs && objs.length > 0) {
            return objs[0].net;
        }
        return undefined;
    }
    addNet(net) {
        const name = this.getNetName(net).valueOf();
        let fullNet = {
            ...net,
            name: this.netRefCtx.existingOrNewRef(name),
        };
        fullNet.netRefs.add(fullNet.name);
        if (!this._nets.has(name)) {
            this._nets.set(name, fullNet);
        }
        else {
            fullNet = this.mergeNets(fullNet, this._nets.get(name));
            this._nets.set(name, fullNet);
        }
        return fullNet;
    }
    getNetName(net) {
        return this.namingStrategy.resolveNetName(net, this.instance);
    }
}
exports.NetCombobulator = NetCombobulator;
//# sourceMappingURL=NetCombobulator.js.map